import { movies } from '@/mocks/movies';
import { tvs } from '@/mocks/tvs';
import { news } from '@/mocks/news';
import type { Media, Movie, News, Tv } from '@/types';

export const REGIONS = ['国产', '欧美', '日韩'];
export const YEARS = ['2025', '2024', '2023'];
export const MOVIE_GENRES = ['动作', '喜剧', '爱情', '科幻', '悬疑', '犯罪', '剧情', '奇幻', '冒险', '武侠', '动画', '家庭', '青春', '运动', '惊悚'];
export const TV_GENRES = ['古装', '历史', '职场', '悬疑', '推理', '爱情', '美食', '科幻', '青春', '剧情', '政治', '家庭', '生活', '奇幻', '冒险', '犯罪', '治愈'];

export function getMovies(): Movie[] {
  return movies as Movie[];
}

export function getTvs(): Tv[] {
  return tvs as Tv[];
}

export function getAllMedia(): Media[] {
  return [...getMovies(), ...getTvs()];
}

export function getMediaByKind(kind: 'movie' | 'tv'): Media[] {
  return kind === 'movie' ? getMovies() : getTvs();
}

export function getMediaById(id: string): Media | undefined {
  return getAllMedia().find((item) => item.id === id);
}

export function getNews(): News[] {
  return news as News[];
}

export function getNewsById(id: string): News | undefined {
  return getNews().find((item) => item.id === id);
}

export interface MediaFilter {
  genres?: string[];
  region?: string;
  year?: string;
  sort?: 'newest' | 'popular';
}

export function filterMedia(items: Media[], filter: MediaFilter): Media[] {
  let result = [...items];

  if (filter.genres && filter.genres.length > 0) {
    result = result.filter((item) => item.genres.some((g) => filter.genres!.includes(g)));
  }
  if (filter.region) {
    result = result.filter((item) => item.region === filter.region);
  }
  if (filter.year) {
    result = result.filter((item) => String(item.year) === filter.year);
  }

  if (filter.sort === 'popular') {
    result.sort((a, b) => b.popularity - a.popularity);
  } else {
    result.sort((a, b) => b.year - a.year || b.popularity - a.popularity);
  }

  return result;
}

export function sortNewsByDate(items: News[], order: 'newest' | 'oldest' = 'newest'): News[] {
  const sorted = [...items].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  return order === 'newest' ? sorted.reverse() : sorted;
}

export function relatedMedia(target: Media, count = 4): Media[] {
  return getAllMedia()
    .filter((item) => item.id !== target.id && item.kind === target.kind)
    .map((item) => {
      const shared = item.genres.filter((g) => target.genres.includes(g)).length;
      return { item, score: shared * 10 + item.popularity / 100 };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, count)
    .map((entry) => entry.item);
}

export function relatedNews(target: News, count = 4): News[] {
  return getNews()
    .filter((item) => item.id !== target.id)
    .map((item) => {
      const shared = item.relatedIds.filter((id) => target.relatedIds.includes(id)).length;
      const sameCat = item.category === target.category ? 2 : 0;
      return { item, score: shared * 3 + sameCat };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, count)
    .map((entry) => entry.item);
}

export function searchNews(keyword: string): News[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return getNews().filter(
    (item) =>
      item.title.toLowerCase().includes(kw) ||
      item.summary.toLowerCase().includes(kw) ||
      item.category.toLowerCase().includes(kw) ||
      item.content.some((p) => p.toLowerCase().includes(kw))
  );
}

export function searchMedia(keyword: string, kind?: 'movie' | 'tv'): Media[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  const pool = kind ? getMediaByKind(kind) : getAllMedia();
  return pool
    .map((item) => {
      let score = 0;
      if (item.title.toLowerCase().includes(kw)) score += 100;
      if (item.director.toLowerCase().includes(kw)) score += 40;
      if (item.cast.some((c) => c.toLowerCase().includes(kw))) score += 40;
      if (item.genres.some((g) => g.toLowerCase().includes(kw))) score += 30;
      if (item.region.toLowerCase().includes(kw)) score += 10;
      return { item, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((entry) => entry.item);
}

export function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

export function mediaPath(item: Media): string {
  return `/detail/${item.kind}/${item.id}`;
}

export function newsPath(item: News): string {
  return `/detail/news/${item.id}`;
}

export const CATEGORY_META: Record<string, { title: string; name: string; description: string }> = {
  movie: {
    title: '电影 - 风驰影院影视分类',
    name: '电影',
    description: '汇集最新热门电影资源，可按类型、地区、年份筛选，快速找到你感兴趣的作品。'
  },
  tv: {
    title: '电视剧 - 风驰影院影视分类',
    name: '电视剧',
    description: '精选最新电视剧集与经典剧作，支持类型、地区、年份筛选，追剧更省心。'
  },
  news: {
    title: '影视资讯 - 风驰影院资讯列表',
    name: '影视资讯',
    description: '权威影视动态、行业观察与深度解读，第一时间了解影视圈的最新消息。'
  }
};